/**
 * LA SOURCE `notifications` DU CENTRE DE COMMUNICATION (chantier
 * `centre-communication`, contrat `packages/shell/src/centre/sources.ts` de
 * sa branche). Ce fichier ne depend pas de cette branche : il reprend la FORME
 * du contrat (memes clefs), que le centre branche tel quel.
 *
 * Ce que le centre lit :
 *   - `SOURCE_NOTIFICATIONS` : la declaration de la source (etat `prete`,
 *     placement `tete`), qui remplace l'emplacement `a-venir` ;
 *   - `nonLusDuCentre(port.nonLus)` : `{ total, parSource }`, `parSource.lia`
 *     etant le point de « Reponse de Lia prete » (rubrique `lia`) ;
 *   - `NotificationsBoite` (zone 5 de l'emplacement) et `ClocheNotifications`
 *     (le dock), dans `./NotificationsBoite.tsx` et `./ClocheNotifications.tsx`.
 * Les nombres viennent de `count()` cote serveur (G15) : `sdk.notifications.boite.observerNonLus`.
 */

import type { NonLusView } from '@taki/ui';

/** La forme d'une source du centre (`SourceDeConversation`), recopiee sans import. */
export interface SourceNotificationsDuCentre {
  id: 'notifications';
  libelle: string;
  etat: 'prete';
  placement: 'tete';
}

export const SOURCE_NOTIFICATIONS: SourceNotificationsDuCentre = {
  id: 'notifications',
  libelle: 'Notifications',
  etat: 'prete',
  placement: 'tete',
};

/** Les non lus vus par le centre : le total (cloche du dock, tete du rail) et le detail par source. */
export interface NonLusDuCentre {
  total: number;
  parSource: { notifications: number; lia: number };
  parRubrique: NonLusView['parRubrique'];
}

export function nonLusDuCentre(nonLus: NonLusView): NonLusDuCentre {
  return {
    total: nonLus.total,
    parSource: { notifications: nonLus.total, lia: 0 },
    parRubrique: nonLus.parRubrique,
  };
}

/** Le texte du badge : rien a zero, `99+` au-dela. */
export function texteDuBadge(total: number): string {
  if (total <= 0) return '';
  return total > 99 ? '99+' : String(total);
}

/** Les rubriques dans l'ordre de la maquette hub-c, avec leur titre de groupe. */
export const RUBRIQUES_DE_LA_BOITE = [
  { id: 'citation', titre: 'Mentions' },
  { id: 'message', titre: 'Autres' },
  { id: 'regle', titre: 'Règles' },
] as const;

/** Le libelle du lieu d'une mention : celui ecrit par le socle conversations (`contexte.lieu`), sinon le genre de conversation. */
export function lieuDeMention(contexte: { type: 'canal' | 'direct' | 'chatter' | 'page'; lieu: string } | null): string {
  if (contexte === null) return '';
  if (contexte.lieu.trim() !== '') return contexte.lieu.trim();
  return { canal: 'Canal', direct: 'Message direct', chatter: 'Fiche', page: 'Page' }[contexte.type];
}

/** « il y a 4 min », « hier, 18:30 » : l'heure relative de la maquette. */
export function quand(date: Date | null, maintenant = new Date()): string {
  if (date === null) return '';
  const minutes = Math.round((maintenant.getTime() - date.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.round(minutes / 60);
  const heure = new Intl.DateTimeFormat('fr-LU', { hour: '2-digit', minute: '2-digit' }).format(date);
  const jour = (d: Date) => d.toISOString().slice(0, 10);
  if (jour(date) === jour(maintenant) || heures < 6) return `il y a ${heures} h`;
  const hier = new Date(maintenant.getTime() - 86400000);
  if (jour(date) === jour(hier)) return `hier, ${heure}`;
  return new Intl.DateTimeFormat('fr-LU', { day: 'numeric', month: 'short' }).format(date);
}
